"use client";
import { Button } from "@/components/ui/button";
import { IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import LoadingSkeleton from "./LoadingSkeleton";
import { IconBrandTwitch } from "@tabler/icons-react";
import { SettingTabs } from "../../content/SettingTabs";
import AdminPopover from "../../content/AdminPopover";
import TwitchOptions, { BlockOptions } from "./tabs/twitch/options";
import BlockLink from "./link";
import { Label } from "@/components/ui/label";
import TwitchCustomization from "./tabs/twitch/customization";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    theme: ITheme;
    newBlock?: string | null;
}
export default function BlockTwitch({
    admin,
    user,
    block,
    update,
    style,
    theme,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [twitch, setTwitch] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [hide, setHide] = React.useState(false);
    React.useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setHide(false);
            try {
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_API_SERVER}/v2/live/twitch/${blockUpdate.url}`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                if (!data.live) {
                    if (blockUpdate.options?.hide_offline) {
                        if (!admin) setHide(true);
                        setLoading(false);
                        setTwitch(null);
                    } else {
                        setLoading(false);
                        setTwitch(null);
                        setHide(true);
                    }
                } else {
                    setTwitch(blockUpdate.url);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
            }
        };
        fetchData();
    }, [blockUpdate, user, admin]);
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Twitch",
            titleOptions: (
                <BlockOptions
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <TwitchOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <TwitchCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={[]}
                        user={user}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            title={`Twitch - ${blockUpdate.url}`}
            onClick={handleExpand}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            {!hide ? (
                <div className={`relative aspect-video w-full min-w-full`}>
                    <div
                        className={`absolute flex w-full min-w-full place-content-center items-center justify-center transition-opacity duration-300 ${loading ? "opacity-100" : "pointer-events-none opacity-0"}`}
                    >
                        <LoadingSkeleton />
                        <IconBrandTwitch
                            className="absolute z-50 animate-bounce rounded-md bg-purple-500 p-1 shadow-xl"
                            size={40}
                        />
                    </div>
                    <div
                        className={`w-full min-w-full transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
                    >
                        {twitch ? (
                            <iframe
                                id="twitch-player"
                                className={
                                    "aspect-video h-full w-full rounded-lg"
                                }
                                src={`https://player.twitch.tv/?channel=${blockUpdate.url}&muted=true&parent=localhost&parent=doras-api.site&parent=doras.to${user.domain.domain ? "&parent=" + user.domain.domain : ""}${admin ? "&autoplay=false" : ""}`}
                                onLoad={() => setLoading(false)}
                                allowFullScreen
                            />
                        ) : (
                            <div
                                className={`absolute flex w-full min-w-full place-content-center items-center justify-center opacity-100 transition-opacity duration-300`}
                            >
                                <LoadingSkeleton nopulse />
                                <div className="absolute flex flex-col items-center">
                                    <IconBrandTwitch
                                        className="z-50 rounded-md bg-purple-500 p-1 shadow-xl"
                                        size={40}
                                    />
                                    <Label className="" variant="headingtoggle">
                                        (Preview)
                                    </Label>
                                    <Label className="" variant="headingtoggle">
                                        Stream will appear here when it's live.
                                    </Label>
                                </div>
                            </div>
                        )}
                        {}
                    </div>
                </div>
            ) : !blockUpdate.options?.hide_offline ? (
                <BlockLink
                    user={user}
                    block={{
                        ...blockUpdate,
                        url: "https://www.twitch.tv/" + blockUpdate.url,
                    }}
                    update={update}
                    admin={false}
                    style={style}
                    assets={[]}
                    theme={theme}
                ></BlockLink>
            ) : admin ? (
                <div
                    className={`absolute flex w-full min-w-full place-content-center items-center justify-center opacity-100 transition-opacity duration-300`}
                >
                    <LoadingSkeleton nopulse />
                    <div className="absolute flex flex-col items-center">
                        <IconBrandTwitch
                            className="z-50 rounded-md bg-purple-500 p-1 shadow-xl"
                            size={40}
                        />
                        <Label className="" variant="headingtoggle">
                            (Preview)
                        </Label>
                        <Label className="" variant="headingtoggle">
                            Stream will appear here when it's live.
                        </Label>
                    </div>
                </div>
            ) : (
                HideElementsByClass(block.id)
            )}
        </AdminPopover>
    );
}
function HideElementsByClass(block_id: string) {
    if (
        window.document &&
        window.document.getElementsByClassName("block-" + block_id)[0]
    ) {
        // Get all elements with the class name 'block-' + block_id
        const elements = document.getElementsByClassName("block-" + block_id);
        // Loop through each element and add the 'hidden' attribute
        for (var i = 0; i < elements.length; i++) {
            elements[i].classList.add("hidden");
        }
        return null;
    }
    return null;
}
